import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import '@xyflow/react/dist/style.css';
import { getCompileStatus } from '../../utils/compileStatus';
import { getUpdatedAtMs } from '../../utils/dateUtils';
import useContextualPopover from '../../hooks/useContextualPopover';
import ContextualPopover from '../../components/ContextualPopover/ContextualPopover';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  AppBar,
  Toolbar,
  IconButton,
  InputBase,
  alpha
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  Visibility as PreviewIcon,
  PlayArrow as CompileIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { DWLFSignalBuilder } from '@andywilliams/dwlf-charting';
import useNodeTypes from '../../hooks/useNodeTypes';
import StrategyBacktestStrip from './StrategyBacktestStrip';
import { customEventsApi } from '../../api/customEvents';
import {
  fetchStrategy,
  createStrategy,
  updateStrategy,
  compileStrategy,
  clearCurrentStrategy,
  clearSuccessMessage
} from './visualStrategiesSlice';
import DesktopBestNotice from '../../components/DesktopBestNotice';

const StrategyBuilderPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { strategyId } = useParams();
  const isEditing = Boolean(strategyId);
  
  const {
    currentStrategy,
    currentStrategyLoading,
    currentStrategyError,
    isCreating,
    isUpdating,
    isCompiling,
    successMessage
  } = useSelector((state) => state.visualStrategies);

  // Local state
  // The node catalogue is the builder's indicator palette, descriptions, firing
  // timeframes and engine-ignored marking (DWLF-370). While it loads the builder
  // says so; if it fails, the palette says the catalogue is unavailable.
  const { catalogue: nodeTypes, loading: nodeTypesLoading } = useNodeTypes();
  const [strategyName, setStrategyName] = useState('');
  const [strategyDescription, setStrategyDescription] = useState('');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [compilePromptOpen, setCompilePromptOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [messageType, setMessageType] = useState('success'); // 'success', 'compilation'
  const [customEventDefinitions, setCustomEventDefinitions] = useState([]);

  // Ref for the signal builder
  const signalBuilderRef = useRef(null);
  const graphLoadTimeoutRef = useRef(null);
  const hasUnsavedChangesRef = useRef(false);
  const importSuppressUntilRef = useRef(0);
  const isSavingRef = useRef(false);
  const lastSavedAtRef = useRef(0);
  const GRAPH_RELOAD_DELAY_MS = 1000;

  // Contextual popover for first strategy builder visit
  const strategyPopover = useContextualPopover('first-strategy-builder-visit');
  const builderContainerRef = useRef(null);

  const isOlderThanLastSaved = (entity) => {
    const updatedAtMs = getUpdatedAtMs(entity);
    if (updatedAtMs === null || lastSavedAtRef.current === 0) {
      return false;
    }
    return updatedAtMs <= lastSavedAtRef.current;
  };

  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  // Show contextual popover on first visit
  useEffect(() => {
    // Destructure to avoid unstable object reference as dependency
    const { show: popoverShow, setAnchorEl: setPopoverAnchor } = strategyPopover;
    if (builderContainerRef.current && !popoverShow && !currentStrategyLoading) {
      const timer = setTimeout(() => {
        setPopoverAnchor(builderContainerRef.current);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [currentStrategyLoading, strategyPopover.show, strategyPopover.setAnchorEl]);

  useEffect(() => {
    lastSavedAtRef.current = 0;
  }, [strategyId]);

  useEffect(() => {
    return () => {
      if (graphLoadTimeoutRef.current) {
        clearTimeout(graphLoadTimeoutRef.current);
      }
    };
  }, []);

  // Handle duplicate from location state
  useEffect(() => {
    if (location.state?.duplicateFrom) {
      const { duplicateFrom } = location.state;
      setStrategyName(location.state.name || `${duplicateFrom.name} (Copy)`);
      setStrategyDescription(duplicateFrom.description || '');
      
      if (graphLoadTimeoutRef.current) {
        clearTimeout(graphLoadTimeoutRef.current);
      }

      // Duplicate flow should always import once; user typing shouldn't cancel the initial visual.
      // Delay loading the visual data to ensure React Flow is ready
      graphLoadTimeoutRef.current = setTimeout(() => {
        if (signalBuilderRef.current && duplicateFrom.visual) {
          const result = signalBuilderRef.current.importStrategy(JSON.stringify(duplicateFrom.visual));
          if (result?.success) {
            importSuppressUntilRef.current = Date.now() + 2000;
          }
        }
      }, GRAPH_RELOAD_DELAY_MS);
    }
  }, [location.state]);

  // Load strategy for editing
  useEffect(() => {
    if (isEditing && strategyId) {
      dispatch(fetchStrategy(strategyId));
    }
    
    return () => {
      dispatch(clearCurrentStrategy());
    };
  }, [dispatch, isEditing, strategyId]);

  useEffect(() => {
    let isActive = true;

    const loadCustomEvents = async () => {
      try {
        const response = await customEventsApi.listEvents({ limit: 200 });
        const items = Array.isArray(response?.events)
          ? response.events
          : response?.customEvents || [];
        if (isActive) {
          setCustomEventDefinitions(items);
        }
      } catch (error) {
        console.warn('Failed to load custom events for strategy builder', error);
        if (isActive) {
          setCustomEventDefinitions([]);
        }
      }
    };

    loadCustomEvents();
    return () => {
      isActive = false;
    };
  }, []);

  // Set form data when strategy loads
  useEffect(() => {
    if (currentStrategy) {
      if (isOlderThanLastSaved(currentStrategy)) {
        return;
      }

      setStrategyName(currentStrategy.name || '');
      setStrategyDescription(currentStrategy.description || '');
      
      if (graphLoadTimeoutRef.current) {
        clearTimeout(graphLoadTimeoutRef.current);
      }

      // Delay loading visual data to avoid reloading stale data immediately after saves.
      graphLoadTimeoutRef.current = setTimeout(() => {
        if (isSavingRef.current) {
          return;
        }
        if (isOlderThanLastSaved(currentStrategy)) {
          return;
        }
        if (hasUnsavedChangesRef.current) {
          return;
        }
        if (signalBuilderRef.current && currentStrategy.visual) {
          const result = signalBuilderRef.current.importStrategy(JSON.stringify(currentStrategy.visual));
          if (result?.success) {
            importSuppressUntilRef.current = Date.now() + 2000;
          }
        }
      }, GRAPH_RELOAD_DELAY_MS);
    }
  }, [currentStrategy]);

  // Handle success messages
  useEffect(() => {
    if (successMessage) {
      setSnackbarOpen(true);
      
      // Set message type for styling
      if (successMessage.includes('compiled')) {
        setMessageType('compilation');
      } else {
        setMessageType('success');
      }
      
      // Only reset unsaved changes for save operations, not compilation
      if (successMessage.includes('saved') || successMessage.includes('created') || successMessage.includes('updated')) {
        setHasUnsavedChanges(false);
        // Show compile prompt after save
        setCompilePromptOpen(true);
        
        // Clear the success message after showing - stay on page, let user decide when to navigate
        setTimeout(() => {
          dispatch(clearSuccessMessage());
          setSnackbarOpen(false);
        }, 3000);
      } else {
        // For other messages (like compilation), just clear after showing
        setTimeout(() => {
          dispatch(clearSuccessMessage());
          setSnackbarOpen(false);
        }, 4000); // Show compilation success longer
      }
    }
  }, [successMessage, dispatch]);

  const handleBack = () => {
    if (hasUnsavedChanges) {
      const confirmLeave = window.confirm('You have unsaved changes. Are you sure you want to leave?');
      if (!confirmLeave) {
        return;
      }
    }
    navigate('/strategies');
  };

  const handleSave = () => {
    if (!strategyName.trim()) {
      alert('Please enter a strategy name');
      return;
    }
    // For new strategies, show dialog to confirm description
    // For editing, save directly if only name changed
    if (!isEditing || strategyDescription !== (currentStrategy?.description || '')) {
      setSaveDialogOpen(true);
    } else {
      handleSaveConfirm();
    }
  };

  const handleSaveConfirm = async () => {
    if (!signalBuilderRef.current) {
      alert('Signal builder not ready');
      return;
    }

    try {
      // Get the current strategy data from the signal builder
      const visual = signalBuilderRef.current.exportStrategy();
      const validationError = validateStrategyGraph(visual);
      if (validationError) {
        alert(validationError);
        return;
      }

      isSavingRef.current = true;
      const strategyData = {
        name: strategyName.trim(),
        description: strategyDescription.trim(),
        version: '1.0.0',
        visual,
        metadata: {
          tags: [], // Could be extracted from form or visual
          timeframe: '1D', // Could be from form
          assets: [], // Could be from form
          isPublic: false,
          isTemplate: false
        }
      };

      let savedStrategy = null;
      if (isEditing) {
        savedStrategy = await dispatch(updateStrategy({ 
          strategyId, 
          updates: strategyData 
        })).unwrap();
      } else {
        // For new strategies, get the result and update the URL so subsequent saves work
        const result = await dispatch(createStrategy(strategyData)).unwrap();
        savedStrategy = result;
        if (result?.strategyId) {
          // Replace the current URL with the edit URL so subsequent saves become updates
          navigate(`/strategies/${result.strategyId}/edit`, { replace: true });
        }
      }

      lastSavedAtRef.current = getUpdatedAtMs(savedStrategy) || 0;
      
      setSaveDialogOpen(false);
    } catch (error) {
      console.error('Error saving strategy:', error);
      alert('Error saving strategy. Please try again.');
    } finally {
      isSavingRef.current = false;
    }
  };

  const validateStrategyGraph = (visual) => {
    const nodes = visual?.nodes || [];
    const customEventRefs = nodes.filter(
      (node) => (node?.data?.nodeType || node?.nodeType) === 'custom_event_reference'
    );
    const missingCustomEvent = customEventRefs.some((node) => !node?.data?.customEventId);
    if (missingCustomEvent) {
      return 'Select a custom event for each "Custom Event" node.';
    }
    return null;
  };

  const handleSaveCancel = () => {
    setSaveDialogOpen(false);
  };

  const handleStrategyChange = (visual) => {
    // Ignore empty-graph notifications from the builder (mount churn, prop-driven
    // re-renders, etc.) — they are not real user edits.
    if (!visual?.nodes?.length) {
      return;
    }
    // During the post-import settlement window, skip only the dirty
    // flag — not the entire callback. If a genuine user edit lands
    // inside the window, it won't get a dirty warning but the edit
    // itself isn't lost.
    if (Date.now() >= importSuppressUntilRef.current) {
      setHasUnsavedChanges(true);
    }
  };

  const handlePreview = () => {
    if (signalBuilderRef.current) {
      const visual = signalBuilderRef.current.exportStrategy();
      console.log('Strategy Preview:', JSON.stringify(visual, null, 2));
      // Could open a preview modal here
    }
  };

  const handleCompile = () => {
    if (!strategyId) {
      alert('Please save the strategy first before compiling');
      return;
    }
    dispatch(compileStrategy(strategyId));
  };

  const handleCompilePromptCompile = () => {
    setCompilePromptOpen(false);
    // Get the current strategyId - for new strategies it will be set by the URL after navigation
    const currentStrategyId = strategyId || currentStrategy?.strategyId;
    if (currentStrategyId) {
      dispatch(compileStrategy(currentStrategyId));
    }
  };

  const handleCompilePromptLater = () => {
    setCompilePromptOpen(false);
  };



  // Determine compile status for warning banners
  const compileStatus = useMemo(() => {
    if (!isEditing || !currentStrategy) {
      return null;
    }
    return getCompileStatus(currentStrategy, { entityType: 'strategy' });
  }, [isEditing, currentStrategy]);

  if (currentStrategyLoading) {
    return (
      <Container maxWidth="xl" style={{ padding: '20px' }}>
        <Typography variant="h6">Loading strategy...</Typography>
      </Container>
    );
  }

  if (currentStrategyError) {
    return (
      <Container maxWidth="xl" style={{ padding: '20px' }}>
        <Alert severity="error">
          {currentStrategyError}
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ 
      height: 'calc(100vh - 64px)', // Account for navbar height
      display: 'flex', 
      flexDirection: 'column', 
      overflow: 'hidden'
    }}>
      {/* Top App Bar */}
      <AppBar position="static" color="default" elevation={1} sx={{ flexShrink: 0 }}>
        <Toolbar>
          <IconButton
            edge="start"
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            <BackIcon />
          </IconButton>
          
          {/* Strategy Name Input */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary', minWidth: 'auto' }}>
              {isEditing ? 'Edit:' : 'New:'}{hasUnsavedChanges && ' *'}
            </Typography>
            <InputBase
              placeholder="Enter strategy name..."
              value={strategyName}
              onChange={(e) => {
                setStrategyName(e.target.value);
                setHasUnsavedChanges(true);
              }}
              sx={{
                flex: 1,
                maxWidth: '400px',
                fontSize: '1.1rem',
                fontWeight: 500,
                color: 'text.primary',
                backgroundColor: alpha('#000', 0.04),
                borderRadius: 1,
                padding: '8px 12px',
                border: '1px solid transparent',
                '&:hover': {
                  backgroundColor: alpha('#000', 0.08),
                },
                '&:focus': {
                  backgroundColor: 'background.paper',
                  borderColor: 'primary.main',
                  outline: 'none',
                },
                '&::placeholder': {
                  color: 'text.disabled',
                  opacity: 1,
                }
              }}
            />
          </Box>
          
          <Button
            startIcon={<PreviewIcon />}
            onClick={handlePreview}
            sx={{ mr: 2 }}
          >
            Preview
          </Button>
          
          {isEditing && (
            <Button
              startIcon={<CompileIcon />}
              onClick={handleCompile}
              disabled={isCompiling || hasUnsavedChanges}
              sx={{ 
                mr: 2,
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.75rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                borderRadius: 0,
                // Ghost, per the BuildEvent/BuildStrategy boards: Save is the one primary action (DWLF-305).
                backgroundColor: 'transparent',
                border: '1px solid var(--dwlf-ink-20)',
                color: 'var(--dwlf-text)',
                '&:hover': {
                  backgroundColor: 'transparent', borderColor: 'var(--dwlf-accent)', color: 'var(--dwlf-accent)',
                },
                '&:disabled': {
                  backgroundColor: 'var(--dwlf-ink-12)',
                  color: 'var(--dwlf-ink-40)'
                }
              }}
            >
              {isCompiling ? 'Compiling...' : 'Compile'}
            </Button>
          )}
          
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={isCreating || isUpdating || !strategyName.trim()}
            sx={{ 
              fontFamily: '"Space Mono", monospace',
              fontSize: '0.75rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              borderRadius: 0,
              backgroundColor: hasUnsavedChanges ? 'var(--dwlf-warn)' : 'var(--dwlf-accent)',
              color: 'var(--dwlf-bg)',
              '&:hover': {
                backgroundColor: hasUnsavedChanges ? '#cc9300' : 'var(--dwlf-text)',
              }
            }}
          >
            {isCreating || isUpdating ? 'Saving...' : hasUnsavedChanges ? 'Save Changes' : 'Save Strategy'}
          </Button>
        </Toolbar>
      </AppBar>

      {compileStatus && !hasUnsavedChanges && (
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          sx={{
            borderRadius: 0,
            flexShrink: 0,
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%'
            }
          }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<CompileIcon />}
              onClick={handleCompile}
              disabled={isCompiling}
              sx={{
                fontWeight: 600,
                backgroundColor: 'var(--dwlf-ink-20)',
                '&:hover': {
                  backgroundColor: 'var(--dwlf-ink-30)'
                }
              }}
            >
              {isCompiling ? 'Compiling...' : 'Compile Now'}
            </Button>
          }
        >
          {compileStatus.message}
        </Alert>
      )}

      {/* Main Content */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        position: 'relative',
        minHeight: 0, // Important for flex child to shrink
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        gap: 2
      }}>
        {/* Signal Builder */}
        <DesktopBestNotice what="The strategy builder" />
        <Box
          ref={builderContainerRef}
          sx={{ 
            flex: 1,
            minHeight: '500px',
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1
          }}
        >
          <DWLFSignalBuilder
            ref={signalBuilderRef}
            onStrategyChange={handleStrategyChange}
            customEvents={customEventDefinitions}
            nodeTypes={nodeTypes}
            nodeTypesLoading={nodeTypesLoading}
            getCustomEventHref={(id) => `/custom-events/${id}/view`}
            style={{
              width: '100%',
              height: '100%'
            }}
          />
        </Box>
        {/* This strategy's latest backtest (DWLF-305) */}
        {strategyId && <StrategyBacktestStrip strategyId={strategyId} />}
      </Box>

      {/* Save Dialog */}
      <Dialog open={saveDialogOpen} onClose={handleSaveCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEditing ? `Update "${strategyName}"` : `Save "${strategyName}"`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Description (Optional)"
              value={strategyDescription}
              onChange={(e) => setStrategyDescription(e.target.value)}
              multiline
              rows={4}
              placeholder="Describe your strategy, its purpose, and when to use it..."
              helperText="A good description helps you and others understand this strategy later"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSaveCancel}>Cancel</Button>
          <Button 
            onClick={handleSaveConfirm} 
            variant="contained"
            disabled={isCreating || isUpdating}
          >
            {isCreating || isUpdating ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Compile Prompt Dialog */}
      <Dialog open={compilePromptOpen} onClose={handleCompilePromptLater} maxWidth="xs" fullWidth>
        <DialogTitle>
          Compile Strategy?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Your changes have been saved. Would you like to compile this strategy now to start generating signals?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCompilePromptLater}>
            Later
          </Button>
          <Button
            onClick={handleCompilePromptCompile}
            variant="contained"
            startIcon={<CompileIcon />}
            disabled={isCompiling}
            sx={{
              background: 'linear-gradient(90deg, #10b981, #059669)',
              '&:hover': {
                background: 'linear-gradient(90deg, #059669, #047857)',
              }
            }}
          >
            {isCompiling ? 'Compiling...' : 'Compile Now'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={messageType === 'compilation' ? 4000 : 2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity="success" 
          sx={{
            backgroundColor: messageType === 'compilation' ? '#10b981' : '#4caf50',
            color: 'white',
            fontWeight: 600,
            fontSize: '1rem',
            '& .MuiAlert-icon': {
              color: 'white'
            },
            '& .MuiAlert-message': {
              color: 'white'
            },
            '& .MuiAlert-action': {
              color: 'white'
            }
          }}
          onClose={() => setSnackbarOpen(false)}
        >
          <span style={{ color: 'white' }}>
            {messageType === 'compilation' ? '🎉 ' : '✅ '}{successMessage}
          </span>
        </Alert>
      </Snackbar>

      <ContextualPopover
        open={strategyPopover.show}
        anchorEl={strategyPopover.anchorEl}
        onDismiss={strategyPopover.dismiss}
        message="Start with a Long Signal node and connect your entry conditions."
        anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
        transformOrigin={{ vertical: 'center', horizontal: 'right' }}
      />
    </Box>
  );
};

export default StrategyBuilderPage; 
